import { useState } from "react";
import type { Currency, Participant, SuggestedTransfer } from "@/api/types";
import { Avatar } from "./Avatar";
import { Button } from "./Button";
import { MoneyAmount } from "./MoneyAmount";
import { ReceiptShareList } from "./ReceiptShareList";
import { participantLabel } from "@/utils/avatar";
import "./transfer-card.css";

export interface TransferCardProps {
  transfer: SuggestedTransfer;
  ccy: Currency;
  provisional: boolean;
  participants: ReadonlyArray<Participant>;
  /** Settle-up Mark-Paid CTA. Disabled while trip is Open (AC-17.4b). */
  onMarkPaid?: () => void;
  /** Optional explicit disabled state for the CTA. */
  markPaidDisabled?: boolean;
  /** Hint shown under the CTA when it's gated. */
  gatedHint?: string;
}

export function TransferCard({
  transfer,
  ccy,
  provisional,
  participants,
  onMarkPaid,
  markPaidDisabled,
  gatedHint,
}: TransferCardProps) {
  const [expanded, setExpanded] = useState(false);
  const from = participants.find((p) => p.participant_id === transfer.from_participant_id);
  const to = participants.find((p) => p.participant_id === transfer.to_participant_id);
  if (!from || !to) return null;

  const breakdownId = `za-shcho-${transfer.from_participant_id}-${transfer.to_participant_id}`;
  const hasShares = transfer.contributing_receipts.length > 0;

  return (
    <article className="wp-transfer" data-testid="transfer-card">
      <div className="wp-transfer__head">
        <div className="wp-transfer__people">
          <Avatar participant={from} size="md" />
          <span className="wp-transfer__name">{participantLabel(from)}</span>
          <span aria-hidden="true" className="wp-transfer__arrow">→</span>
          <Avatar participant={to} size="md" />
          <span className="wp-transfer__name">{participantLabel(to)}</span>
        </div>
        <div className="wp-transfer__amount">
          <MoneyAmount
            value={transfer.amount}
            ccy={ccy}
            size="h1"
            provisional={provisional}
            tone="owed"
          />
        </div>
      </div>
      {onMarkPaid ? (
        <div className="wp-transfer__cta">
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={onMarkPaid}
            disabled={markPaidDisabled}
          >
            Позначити сплачено
          </Button>
          {gatedHint ? <p className="wp-transfer__hint">{gatedHint}</p> : null}
        </div>
      ) : null}
      {/* "За що" — the single fairness/audit affordance (AC-16.6, ADR-0025). */}
      <button
        type="button"
        className="wp-transfer__disclosure"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={breakdownId}
      >
        <span>За що</span>
        <span aria-hidden="true">{expanded ? "⌃" : "⌄"}</span>
      </button>
      {expanded ? (
        <div id={breakdownId}>
          {hasShares ? (
            <ReceiptShareList
              shares={transfer.contributing_receipts}
              ccy={ccy}
              provisional={provisional}
            />
          ) : (
            <p className="wp-transfer__hint">Деталі недоступні</p>
          )}
        </div>
      ) : null}
    </article>
  );
}
